// src/services/sessionManager.js
import authService from './servicioAutenticacion'

// Debounce para eventos frecuentes (mousemove)
const DEBOUNCE_MS = 500

/**
 * Lógica basada en la presencia del mouse en la ventana del navegador:
 *
 * FASE 1 — Vigilancia (mouse en ventana):
 *   - Mientras el mouse esté dentro de la ventana, no corre ningún timer.
 *   - Cuando el mouse SALE de la ventana → empieza el timer de 5 min.
 *   - Si el mouse VUELVE antes de 5 min → se cancela el timer (reset).
 *   - Actividad de teclado/touch también cancela el timer (usuario activo).
 *
 * FASE 2 — Cuenta regresiva (60 min fijos):
 *   - El mouse lleva 5 min fuera de la ventana → arranca la cuenta de 60 min.
 *   - Si el mouse VUELVE durante los 60 min (antes del modal) → reset total.
 *   - Al minuto 59 → modal de advertencia (1 min de cuenta regresiva).
 *   - Con el modal visible → solo el botón "Mantener Conectado" cancela.
 *   - Al minuto 60 → cierre de sesión automático.
 *
 * "Mantener Conectado" → vuelve a Fase 1.
 */
class SessionManager {
  constructor() {
    // Fase 1: cuánto tiempo sin mouse en ventana antes de iniciar cuenta regresiva
    this.TIEMPO_INACTIVIDAD = 5 * 60 * 1000  // 5 min mouse fuera → Fase 2

    // Fase 2: cuenta regresiva
    this.TIEMPO_ADVERTENCIA = 59 * 60 * 1000 // min 59 → mostrar modal
    this.TIEMPO_EXPIRACION  = 60 * 60 * 1000 // min 60 → cerrar sesión

    this.warningShown        = false
    this.isMonitoring        = false
    this._faseExpiracion     = false
    this._ultimoEvento       = 0

    // Timers
    this._timerInactividad = null
    this._timerAdvertencia = null
    this._timerExpiracion  = null

    this.onWarningCallback      = null
    this.onExpiryCallback       = null

    // Manejadores de eventos (guardados para poder removerlos)
    this._manejadorMouseSale    = null  // mouse sale de la ventana
    this._manejadorMouseEntra   = null  // mouse entra / se mueve en la ventana
    this._manejadorTeclado      = null  // teclado / touch (usuario activo)
    this._manejadorVisibilidad  = null  // tab visible/oculta
  }

  // ─────────────────────────────────────────────
  // API pública
  // ─────────────────────────────────────────────

  startMonitoring(onWarning, onExpiry) {
    // DESACTIVADO TEMPORALMENTE: el cierre de sesión por inactividad está pausado
    return // eslint-disable-line no-unreachable

    if (this.isMonitoring) this.stopMonitoring() // eslint-disable-line no-unreachable

    this.onWarningCallback = onWarning
    this.onExpiryCallback  = onExpiry
    this.isMonitoring      = true
    this._faseExpiracion   = false
    this.warningShown      = false
    this._ultimoEvento     = 0

    this._adjuntarEscuchas()
    // Arrancar timer de inactividad desde inicio (no sabemos si el mouse está en ventana)
    this._iniciarTimerInactividad()
  }

  stopMonitoring() {
    this._limpiarTodosLosTimers()
    this._retirarEscuchas()
    this.warningShown    = false
    this.isMonitoring    = false
    this._faseExpiracion = false
    this._ultimoEvento   = 0
  }

  /** El usuario confirmó mantener la sesión → volver a Fase 1 */
  extendSession() {
    if (this.isMonitoring) this._resetTotal()
  }

  /** Alias de compatibilidad (tras refrescar token) */
  resetTimers() {
    if (this.isMonitoring) this._resetTotal()
  }

  /** Alias de compatibilidad */
  clearTimers() {
    this._limpiarTodosLosTimers()
  }

  /** Fuerza el cierre de sesión */
  expireSession() {
    this._limpiarTodosLosTimers()
    this._retirarEscuchas()
    this.isMonitoring    = false
    this._faseExpiracion = false
    if (this.onExpiryCallback) {
      this.onExpiryCallback()
    } else {
      authService.cerrarSesion()
    }
  }

  // ─────────────────────────────────────────────
  // Lógica de fases
  // ─────────────────────────────────────────────

  /**
   * Reinicio total → cancela todo y arranca el timer de inactividad de 5 min.
   * Se llama cuando el mouse entra a la ventana o hay actividad de teclado/touch.
   */
  _resetTotal() {
    this._limpiarTodosLosTimers()
    this._faseExpiracion = false
    this.warningShown    = false
    // Cuando el mouse está dentro, no hay timer corriendo.
    // El timer empieza cuando el mouse salga (ver _alMouseSalir).
  }

  /** Arranca (o reinicia) el timer de 5 min de inactividad (mouse fuera). */
  _iniciarTimerInactividad() {
    this._limpiarTimerInactividad()
    if (this._faseExpiracion) return // ya estamos en Fase 2, no sobrescribir
    this._timerInactividad = setTimeout(() => this._iniciarFase2(), this.TIEMPO_INACTIVIDAD)
  }

  /** El mouse lleva 5 min fuera → arranca la cuenta regresiva de 60 min */
  _iniciarFase2() {
    this._limpiarTimerInactividad()
    this._faseExpiracion = true

    this._timerAdvertencia = setTimeout(() => this._mostrarAdvertencia(), this.TIEMPO_ADVERTENCIA)
    this._timerExpiracion  = setTimeout(() => this.expireSession(),       this.TIEMPO_EXPIRACION)
  }

  _mostrarAdvertencia() {
    if (this.warningShown) return
    this.warningShown = true
    if (this.onWarningCallback) this.onWarningCallback()
  }

  // ─────────────────────────────────────────────
  // Manejadores de eventos
  // ─────────────────────────────────────────────

  /**
   * Mouse SALE de la ventana del navegador.
   * Si el modal no está visible → empieza (o reinicia) el timer de 5 min.
   */
  _alMouseSalir() {
    if (!this.isMonitoring || this.warningShown) return
    if (!this._faseExpiracion) {
      this._iniciarTimerInactividad()
    }
  }

  /**
   * Mouse ENTRA / SE MUEVE dentro de la ventana.
   * Si el modal no está visible → reset total (cancelar cualquier timer activo).
   */
  _alMouseEntrar() {
    if (!this.isMonitoring) return
    const ahora = Date.now()
    if (ahora - this._ultimoEvento < DEBOUNCE_MS) return
    this._ultimoEvento = ahora
    if (this.warningShown) return // modal visible → usar el botón
    this._resetTotal()
  }

  /**
   * Actividad de teclado o touch → usuario activo aunque el mouse esté fuera.
   * Si el modal no está visible → reset total.
   */
  _alActividad() {
    if (!this.isMonitoring) return
    const ahora = Date.now()
    if (ahora - this._ultimoEvento < DEBOUNCE_MS) return
    this._ultimoEvento = ahora
    if (this.warningShown) return
    this._resetTotal()
  }

  // ─────────────────────────────────────────────
  // Adjuntar / retirar escuchas
  // ─────────────────────────────────────────────

  _adjuntarEscuchas() {
    // Mouse sale de la ventana — usar 'mouseout' con relatedTarget===null es más fiable
    // que 'mouseleave' en todos los navegadores
    this._manejadorMouseSale = (e) => {
      if (e.relatedTarget === null || e.relatedTarget === undefined) {
        this._alMouseSalir()
      }
    }
    document.addEventListener('mouseout', this._manejadorMouseSale)

    // Mouse entra / se mueve dentro de la ventana
    this._manejadorMouseEntra = () => this._alMouseEntrar()
    document.addEventListener('mousemove',  this._manejadorMouseEntra, { passive: true })
    document.addEventListener('mouseenter', this._manejadorMouseEntra, { passive: true })

    // Teclado y touch (el usuario está activo aunque el mouse esté fuera)
    this._manejadorTeclado = () => this._alActividad()
    document.addEventListener('keydown',    this._manejadorTeclado, { passive: true })
    document.addEventListener('touchstart', this._manejadorTeclado, { passive: true })

    // Visibilidad de pestaña
    this._manejadorVisibilidad = () => {
      if (!this.isMonitoring) return
      if (document.hidden) {
        // Pestaña oculta → como si el mouse saliera: iniciar timer de inactividad
        if (!this.warningShown && !this._faseExpiracion) {
          this._iniciarTimerInactividad()
        }
      } else {
        // Pestaña visible de nuevo → como si el mouse entrara
        if (!this.warningShown) this._resetTotal()
      }
    }
    document.addEventListener('visibilitychange', this._manejadorVisibilidad)
  }

  _retirarEscuchas() {
    if (this._manejadorMouseSale) {
      document.removeEventListener('mouseout',     this._manejadorMouseSale)
      this._manejadorMouseSale = null
    }
    if (this._manejadorMouseEntra) {
      document.removeEventListener('mousemove',    this._manejadorMouseEntra)
      document.removeEventListener('mouseenter',   this._manejadorMouseEntra)
      this._manejadorMouseEntra = null
    }
    if (this._manejadorTeclado) {
      document.removeEventListener('keydown',      this._manejadorTeclado)
      document.removeEventListener('touchstart',   this._manejadorTeclado)
      this._manejadorTeclado = null
    }
    if (this._manejadorVisibilidad) {
      document.removeEventListener('visibilitychange', this._manejadorVisibilidad)
      this._manejadorVisibilidad = null
    }
  }

  // ─────────────────────────────────────────────
  // Limpieza de timers
  // ─────────────────────────────────────────────

  _limpiarTimerInactividad() {
    if (this._timerInactividad) {
      clearTimeout(this._timerInactividad)
      this._timerInactividad = null
    }
  }

  _limpiarTimersExpiracion() {
    if (this._timerAdvertencia) {
      clearTimeout(this._timerAdvertencia)
      this._timerAdvertencia = null
    }
    if (this._timerExpiracion) {
      clearTimeout(this._timerExpiracion)
      this._timerExpiracion = null
    }
  }

  _limpiarTodosLosTimers() {
    this._limpiarTimerInactividad()
    this._limpiarTimersExpiracion()
  }

  // ─────────────────────────────────────────────
  // Utilidades JWT
  // ─────────────────────────────────────────────

  isSessionExpiringSoon() {
    const token = authService.obtenerTokenAcceso()
    if (!token) return false
    try {
      const payload = this.decodeJwt(token)
      if (!payload || !payload.exp) return false
      return (payload.exp * 1000 - Date.now()) < (10 * 60 * 1000)
    } catch {
      return false
    }
  }

  decodeJwt(token) {
    try {
      const parts = token.split('.')
      if (parts.length !== 3) return null
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/')
      const json = decodeURIComponent(
        atob(payload)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      )
      return JSON.parse(json)
    } catch {
      return null
    }
  }
}

// Exportar una instancia singleton
export const sessionManager = new SessionManager()
export default sessionManager
